import React, { useState, useEffect } from "react";
import PageHelmet from "../components/PageHelmet.jsx";
import CustomDataGrid from "../components/CustomDataGrid.jsx";
import {
    generateGridRows,
    generateGridColumns,
    existingExtraCreditsGridColumns,
    existingExtraCreditsRowMapping,
    generateFormData,
    extraCreditsDetailsMapping
} from "../utils/PageUtils.js";
import EntityDetails from "../components/EntityDetails.jsx";
import '../styles/Activities.css';
import { MdArrowBackIos } from "react-icons/md";
import { Alert } from "react-bootstrap";
import { fetchExtraCredits } from "../utils/api.js";

function ExtraCredits({ userRole, bearerToken }) {
    const [extraCredits, setExtraCredits] = useState([]);
    const [extraCreditsError, setExtraCreditsError] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedExtraCredit, setSelectedExtraCredit] = useState(null);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const getExtraCredits = async () => {
            try {
                const response = await fetchExtraCredits(bearerToken)
                if (response.success) {
                    const sortedRecords = (response.data?.records || []).sort(
                        (a, b) => new Date(b.created) - new Date(a.created)
                    );
                    setExtraCredits(sortedRecords);
                } else {
                    setExtraCreditsError(response.message || "Failed to fetch extra credits");
                }
            } catch {
                setExtraCreditsError("Error while fetching extra credits")
            }
        }
        getExtraCredits();
    }, [])

    const extraCreditRows = generateGridRows(extraCredits, existingExtraCreditsRowMapping);
    const extraCreditColumns = generateGridColumns(
        existingExtraCreditsGridColumns,
        "extra_credit_id",
        "12px",
        {}
    );
    const extraCreditDetails = selectedExtraCredit ? generateFormData(selectedExtraCredit, extraCreditsDetailsMapping) : null;

    const handleRowClick = (params) => {
        setExtraCreditsError(null);
        if (selectedRowId === params.id) {
            setSelectedRowId(null);
            setSelectedExtraCredit(null);
            return;
        }
        const extraCredit = extraCredits[params.id - 1];
        setSelectedExtraCredit(extraCredit);
        setSelectedRowId(params.id);
    }

    const handleCloseDetails = () => {
        setSelectedExtraCredit(null);
        setSelectedRowId(null);
    }

    if (userRole !== "customer") {
        return (
            <div className="user-error">
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
            </div>
        );
    }

    return (
        <div style={{ margin: "20px" }}>
            <PageHelmet
                title="Extra Credits"
                description="Track user activities and actions within the application"
                url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/extra-credits`}
            />
            {extraCreditsError ? (
                <div>
                    <Alert variant="danger">{extraCreditsError}</Alert>
                </div>
            )
                : (
                    <div className={`activities-container ${selectedExtraCredit ? 'with-details' : 'centered'}`}>
                        <div className="activity-datagrid">
                            <h3 className="title-color">Extra Credits</h3>
                            <CustomDataGrid
                                rows={extraCreditRows}
                                columns={extraCreditColumns}
                                totalRows={extraCreditRows.length}
                                onRowClick={handleRowClick}
                                selectedRowId={selectedRowId}
                                getRowHeight={() => "auto"}
                            />
                        </div>

                        {/* Desktop details */}
                        {!isMobile && selectedExtraCredit && (
                            <div className="activity-details">
                                <h3 style={{ fontWeight: "bolder" }}>Extra Credit Details</h3>
                                <EntityDetails inputs={extraCreditDetails} width={"135px"} />
                            </div>
                        )}

                        {/* Mobile full-screen details */}
                        {isMobile && selectedExtraCredit && (
                            <div className="activity-details mobile-open">
                                <div className="activity-details-header">
                                    <div className="mobile-detail-nav">
                                        <button className="back-btn" onClick={handleCloseDetails} aria-label="Back">
                                            <MdArrowBackIos size={18} />
                                        </button>
                                        <div className="mobile-activity-title">{selectedExtraCredit?.extra_credit_id || 'Extra Credit Details'}</div>
                                    </div>
                                </div>
                                <div className="activity-details-body">
                                    <EntityDetails inputs={extraCreditDetails} width={"135px"} />
                                </div>
                            </div>
                        )}
                    </div>
                )}
        </div>
    )
}

export default ExtraCredits;